/**
 * Dashboard Customizer — let the user toggle widget visibility and reorder
 * them via HTML5 drag & drop. Persists via PUT /api/dashboard/widgets.
 *
 * Touch users get explicit Up/Down buttons because mobile browsers do not
 * implement drag events reliably on lists. Both paths converge on the same
 * `move(from, to)` function.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, ChevronUp, ChevronDown, GripVertical, RotateCcw, Loader2, Save, Lock } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";

export default function DashboardCustomize() {
  const navigate = useNavigate();
  const [widgets, setWidgets] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragKey = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/widgets");
        setWidgets(data.widgets || []);
      } catch {
        toast.error("No se pudieron cargar los widgets");
        setWidgets([]);
      }
    })();
  }, []);

  const move = (from, to) => {
    setWidgets(curr => {
      const next = [...curr];
      const [item] = next.splice(from, 1);
      const targetIdx = Math.max(0, Math.min(next.length, to));
      next.splice(targetIdx, 0, item);
      return next;
    });
  };

  const toggle = (key) => {
    setWidgets(curr => curr.map(w => w.key === key ? { ...w, visible: !w.visible } : w));
  };

  const onDragStart = (e, key, idx) => {
    dragKey.current = { key, idx };
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", key); } catch { /* Safari */ }
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, overIdx) => {
    e.preventDefault();
    const src = dragKey.current;
    if (!src) return;
    if (src.idx !== overIdx) move(src.idx, overIdx);
    dragKey.current = null;
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/dashboard/widgets", { widgets });
      toast.success("Personalización guardada");
      window.dispatchEvent(new Event("dashboard-layout-updated"));
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.put("/dashboard/widgets", { widgets: [] });
      const { data } = await api.get("/dashboard/widgets");
      setWidgets(data.widgets || []);
      toast.success("Restablecido al orden por defecto");
      window.dispatchEvent(new Event("dashboard-layout-updated"));
    } catch {
      toast.error("No se pudo restablecer");
    } finally {
      setSaving(false);
    }
  };

  if (widgets === null) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5 pb-8" data-testid="dashboard-customize-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" asChild className="-ml-2">
            <Link to="/" data-testid="customize-back">
              <ArrowLeft className="h-4 w-4 mr-1" /> Volver al Dashboard
            </Link>
          </Button>
          <h1 className="text-3xl sm:text-4xl font-black mt-2" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Personalizar Dashboard
          </h1>
          <p className="text-sm text-slate-500 mt-1">Arrastrá los bloques para reordenarlos. Apagá los que no necesités.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} disabled={saving} data-testid="customize-reset">
            <RotateCcw className="h-4 w-4 mr-2" />Restablecer
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B1F3A] hover:bg-[#152C57] text-white" data-testid="customize-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Guardar
          </Button>
        </div>
      </div>

      <Card className="bg-white border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid="widget-list">
        {widgets.map((w, idx) => {
          const locked = !w.removable;
          return (
            <div
              key={w.key}
              draggable={!locked}
              onDragStart={(e) => !locked && onDragStart(e, w.key, idx)}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, idx)}
              className={`flex items-center gap-3 p-3 sm:p-4 transition-colors ${dragKey.current?.key === w.key ? "bg-blue-50" : "hover:bg-slate-50"}`}
              data-testid={`widget-row-${w.key}`}
            >
              <div className={`shrink-0 ${locked ? "cursor-not-allowed text-slate-300" : "cursor-grab text-slate-400 hover:text-slate-700"}`} title={locked ? "Este bloque no se puede mover" : "Arrastrá para reordenar"}>
                {locked ? <Lock className="h-5 w-5" /> : <GripVertical className="h-5 w-5" />}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">{w.label}</span>
                  {locked && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded-sm">Fijo</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{w.description}</p>
              </div>

              {/* Mobile-friendly reorder buttons */}
              <div className="hidden sm:flex flex-col gap-0.5">
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={locked || idx === 0} onClick={() => move(idx, idx - 1)} data-testid={`widget-up-${w.key}`}>
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7" disabled={locked || idx === widgets.length - 1} onClick={() => move(idx, idx + 1)} data-testid={`widget-down-${w.key}`}>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex sm:hidden gap-1">
                <Button size="icon" variant="ghost" className="h-9 w-9" disabled={locked || idx === 0} onClick={() => move(idx, idx - 1)} aria-label="Mover arriba">
                  <ChevronUp className="h-5 w-5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-9 w-9" disabled={locked || idx === widgets.length - 1} onClick={() => move(idx, idx + 1)} aria-label="Mover abajo">
                  <ChevronDown className="h-5 w-5" />
                </Button>
              </div>

              <Switch
                checked={w.visible !== false}
                onCheckedChange={() => !locked && toggle(w.key)}
                disabled={locked}
                data-testid={`widget-toggle-${w.key}`}
                aria-label={`Mostrar ${w.label}`}
              />
            </div>
          );
        })}
      </Card>

      <p className="text-xs text-slate-500 px-1">
        Los KPIs principales siempre se muestran y no se pueden mover (resumen mínimo del negocio).
        Los cambios se aplican inmediatamente en tu Dashboard sin necesidad de recargar.
      </p>
    </div>
  );
}
